import { useMemo } from 'react';
import PropTypes from 'prop-types';

import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import { Box, Divider, Typography } from '@mui/material';
import { Radio } from 'antd';
import styled from 'styled-components';

import { useDispatch, useSelector } from 'react-redux';
import { updateCostSettings, updateGeneralSetting } from 'store/reducers/inputReducer.js';
import { selectCostSetting } from 'store/reducers/selectors/settingsSelectors.js';
import { selectCoordinateSystem } from 'store/reducers/selectors';
import { selectRoadNetworkAccess, useFeature } from 'store/functions/entitlements';
import { resolveCostMethod } from 'store/functions/costMethods';
import { FEATURE_ROAD_NETWORK_COSTS } from 'functions/entitlements/featureEntitlements';

import InfoIcon from 'components/common/icons/InfoIcon';
import PremiumLock from 'components/common/PremiumLock';

const costSettingParams = require('store/data/cost_setting_params.json');

// Kept next to each other so the badge tooltip and the inline note cannot drift.
const LOCKED_METHOD_MESSAGE =
  'Road-network distances are a Professional-plan feature — upgrade to unlock.';
const LOCKED_METHOD_NOTE = 'Professional plan required';

const Container = styled.div`
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

const SectionHeader = styled.div`
  padding: 14px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 20px;
  cursor: ${props => props.$locked ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$locked ? 0.55 : 1};
  transition: background 0.15s ease;
  border-bottom: 1px solid ${props => props.$last ? 'transparent' : '#f5f5f5'};
  background: ${props => props.$active ? 'rgba(0, 146, 139, 0.04)' : 'transparent'};

  &:hover {
    background: ${props => props.$active ? 'rgba(0, 146, 139, 0.06)' : '#fafafa'};
  }
`;

const CostSettings = (props) => {
  const dispatch = useDispatch();

  const costSettings = costSettingParams[props.costMetric];
  const autoCompute = useSelector(selectCostSetting({ settingName: 'autoCompute', subSettingName: props.costMetric }));
  const savedMethod = useSelector(selectCostSetting({ settingName: costSettings.methodName }));
  const coordinateSystem = useSelector(selectCoordinateSystem);
  // Road-network distance/time (the `driving` method) uses the routing engine and
  // is a Professional-plan feature. Fails open, so this is inert until the backend
  // registry adds `road_network_costs` and a plan marks it false — no method is
  // locked before then.
  const roadDistanceAllowed = useFeature(FEATURE_ROAD_NETWORK_COSTS).allowed;
  // Show the method a calculation will actually use. Without road-network access
  // a saved 'driving' is calculated straight-line (store/functions/costMethods),
  // so that is the option shown as selected; the saved value is left alone.
  const roadNetworkAccess = useSelector(selectRoadNetworkAccess);
  const selectedMethod = resolveCostMethod(savedMethod, roadNetworkAccess);

  const methodOptions = useMemo(() => {
    const methodTypes = costSettings?.methodTypes || {};
    return Object.keys(methodTypes)
      // A method without a coordinateSystems tag applies everywhere; tagged
      // methods only show for matching models (e.g. driving/geo are
      // lat/lng-based and hidden on cartesian x-y models, euclidean is
      // cartesian-only).
      .filter((methodType) => {
        const systems = methodTypes[methodType].coordinateSystems;
        return !systems || systems.includes(coordinateSystem);
      })
      .map((methodType) => {
        const method = methodTypes[methodType];
        return {
          value: methodType,
          label: method.label,
          description: method.description,
          tooltip: method.tooltip,
        };
      });
  }, [costSettings, coordinateSystem]);

  const handleModeChange = (value) => {
    const nextAutoCompute = value === 'auto';
    dispatch(updateCostSettings({ settingName: 'autoCompute', subsettingName: props.costMetric, value: nextAutoCompute }));
  };

  const handleMethodChange = (value) => {
    if (!costSettings?.methodName) return;
    // A locked method never reaches the store, whichever control was clicked.
    // The row and the radio both disable themselves, so this only fires if a
    // future edit adds a third way in.
    if (value === 'driving' && !roadDistanceAllowed) return;
    dispatch(updateGeneralSetting({ settingName: 'costs', subSettingName: costSettings.methodName, value }));
  };

  if (!costSettings) return null;

  const modeOptions = [
    { value: 'auto', label: costSettings.autoComputeLabel, tooltip: costSettings.autoComputeTooltip },
    { value: 'manual', label: costSettings.manualComputeLabel, tooltip: costSettings.manualComputeTooltip },
  ];
  const currentMode = autoCompute ? 'auto' : 'manual';

  return (
    <Container>
      <SectionHeader>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#32325d', mb: 0.25 }}>
          Calculation mode
        </Typography>
        <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.8rem' }}>
          Choose whether Optimiciti should compute costs automatically or manually.
        </Typography>
      </SectionHeader>

      {modeOptions.map((opt, idx) => (
        <OptionRow
          key={opt.value}
          $active={currentMode === opt.value}
          $last={idx === modeOptions.length - 1 && !autoCompute}
          onClick={() => handleModeChange(opt.value)}
        >
          <Radio
            checked={currentMode === opt.value}
            onChange={() => handleModeChange(opt.value)}
            style={{ flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: currentMode === opt.value ? '#017C76' : '#32325d', lineHeight: 1.3 }}>
              {opt.label}
            </Typography>
          </Box>
          {opt.tooltip && (
            <Tooltip title={opt.tooltip} placement="top">
              <InfoIcon />
            </Tooltip>
          )}
        </OptionRow>
      ))}

      <Collapse in={Boolean(autoCompute)} unmountOnExit>
        <Divider />
        <SectionHeader>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#32325d', mb: 0.25 }}>
            {props.radioLabel || 'Calculation method'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.8rem' }}>
            Select a method for calculating travel {props.costMetric}.
          </Typography>
        </SectionHeader>
        {methodOptions.map((opt, idx) => {
          // The road-network method is Professional-only. When locked the row
          // refuses in place — it does NOT navigate. This used to `history.push`
          // to /pricing, which unmounted the wizard and dropped the visitor back
          // at question 1 with every answer re-asked. The gated checkboxes three
          // steps later (wizard/ColumnChoiceCheckbox) already refused in place;
          // this is the same treatment, so the two stop disagreeing.
          const locked = opt.value === 'driving' && !roadDistanceAllowed;
          const active = !locked && selectedMethod === opt.value;
          return (
            <OptionRow
              key={opt.value}
              $active={active}
              $last={idx === methodOptions.length - 1}
              $locked={locked}
              aria-disabled={locked || undefined}
              onClick={locked ? undefined : () => handleMethodChange(opt.value)}
            >
              <Radio
                checked={active}
                disabled={locked}
                onChange={() => handleMethodChange(opt.value)}
                style={{ flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: active ? '#017C76' : '#32325d', lineHeight: 1.3 }}>
                  {opt.label}
                </Typography>
                {/* Shown inline, not just in the hover tooltip: these method
                    names mean nothing to someone new to optimization, and a
                    tooltip they have to discover is no help while choosing. */}
                {opt.description && (
                  <Typography variant="body2" sx={{ color: '#525f7f', fontSize: '0.78rem' }}>
                    {opt.description}
                  </Typography>
                )}
                {/* Said on the row, not only in a hover tooltip: the reason the
                    option will not respond has to be readable by someone who
                    never hovers. Matches ColumnChoiceCheckbox's locked note. */}
                {locked && (
                  <Typography variant="body2" sx={{ color: '#fb6340', fontSize: '0.75rem', fontWeight: 600, mt: 0.4 }}>
                    {LOCKED_METHOD_NOTE}
                  </Typography>
                )}
              </Box>
              {locked ? (
                <PremiumLock
                  interactive={false}
                  tooltip={LOCKED_METHOD_MESSAGE}
                />
              ) : (
                opt.tooltip && (
                  <Tooltip title={opt.tooltip} placement="top">
                    <InfoIcon />
                  </Tooltip>
                )
              )}
            </OptionRow>
          );
        })}
      </Collapse>
    </Container>
  );
};

CostSettings.propTypes = {
  costMetric: PropTypes.string.isRequired,
  radioLabel: PropTypes.string,
};

export default CostSettings;
